import { Injectable } from '@angular/core';

import { Socket } from 'socket.io-client';
import {
  Observable,
  BehaviorSubject,
  finalize,
  filter,
  share,
  take,
  tap,
  from,
  switchMap,
  map,
  Subject,
  Subscriber,
  Subscription,
} from 'rxjs';

import { NestedEvent, WebSocketEventsEnum } from '@shared/websocket';
import { LoggerService } from '@tablesready/frontend-logger';

export interface WebSocketPayloadWithSequence {
  sequenceNumber: number;
  date: string;
  data: any;
}

@Injectable()
export abstract class BaseSocketService {
  protected readonly socketSubject = new BehaviorSubject<Socket | null>(null);
  private connectionPending = false;
  private connectionAttempt = 0;
  private connectionSubscription: Subscription | null = null;
  // How long after `connect` to wait for the backend `authenticated` ack before assuming a legacy
  // backend that predates the ack. TODO: remove (with the fallback below) once the gateway emit is
  // live everywhere, including for the oldest supported iOS build.
  private readonly authenticatedAckTimeoutMs = 5000;
  // socket channels cache
  private readonly events$ = new Subject<[WebSocketEventsEnum, any[]]>();
  private readonly events = this.events$
    .pipe(
      switchMap(([event, data]) => {
        if (event === WebSocketEventsEnum.events) {
          return from(data as NestedEvent[]).pipe(
            map(({event, data}) => ([event, data])),
          );
        }

        return from([[event, data]]);
      }),
      share(),
    );

  private readonly onConnectionErrorSubject = new Subject<void>();
  readonly onConnectionError = this.onConnectionErrorSubject.asObservable();

  private readonly onAuthenticatedSubject = new Subject<void>();
  readonly onAuthenticated = this.onAuthenticatedSubject.asObservable();

  private readonly onSessionRevokedSubject = new Subject<void>();
  readonly onSessionRevoked = this.onSessionRevokedSubject.asObservable();

  // socket creation logic
  protected abstract createSocket(): Observable<Socket>;

  private readonly sequenceMetadata$ = new Subject<{
    event: WebSocketEventsEnum;
    sequenceNumber: number;
    date: string;
  }>();

  readonly onSequenceMetadata = this.sequenceMetadata$.asObservable();

  constructor(protected readonly logger: LoggerService) { }

  getEventStream<T = any>(event: string): Observable<T> {
    return this.events.pipe(
      filter(([_event]) => _event === event),
      map(([, data]) => data),
    );
  }

  /**
   * Public read of the live connection state — lets callers (e.g. a periodic health-check in a
   * component) decide whether to trigger recovery without reaching through the protected
   * `socketSubject`.
   */
  isConnected(): boolean {
    return this.socketSubject.getValue()?.connected ?? false;
  }

  connect(): void {
    const currentSocket = this.socketSubject.getValue();
    if (currentSocket || this.connectionPending) {
      return;
    }

    this.connectionPending = true;
    const connectionAttempt = this.connectionAttempt + 1;
    this.connectionAttempt = connectionAttempt;
    this.connectionSubscription = this.createSocket()
      .pipe(
        take(1),
        tap((createdSocket) => {
          if (connectionAttempt !== this.connectionAttempt) {
            createdSocket.disconnect();
            return;
          }

          createdSocket.onAny((socketEvent, payload) => {
            if (this.isWrappedPayload(payload)) {
              const { sequenceNumber, date, data } = payload;

              this.sequenceMetadata$.next({
                event: socketEvent,
                sequenceNumber,
                date,
              });

              this.events$.next([socketEvent, data]);
            } else {
              this.events$.next([socketEvent, payload]);
            }
          });

          this.socketSubject.next(createdSocket);
        }),
        finalize(() => {
          if (connectionAttempt === this.connectionAttempt) {
            this.connectionPending = false;
          }
        }),
      )
      .subscribe({
        // createSocket() can error before producing a socket (e.g. the public app's anon-token
        // fetch failing while offline). Swallow it: `connectionPending` resets via finalize, so the
        // next recovery trigger (resume, health-check) simply retries.
        error: err => this.logger.info('Socket connection attempt failed:', err?.message ?? err),
      });
  }

  disconnect(): void {
    this.connectionAttempt += 1;
    this.connectionPending = false;
    // Tear down an in-flight connection attempt: when the socket hasn't fired `connect` yet it is
    // not in `socketSubject`, so only unsubscribing (which runs createSocket's teardown) stops it.
    this.connectionSubscription?.unsubscribe();
    this.connectionSubscription = null;
    const socket = this.socketSubject.getValue();
    if (socket) {
      socket.disconnect();
    }
    this.socketSubject.next(null);
  }

  /**
   * Re-establishes the socket when it is not connected — used on tab resume / back-online and by
   * periodic health-checks, where a long freeze may have left the socket given up or
   * server-disconnected (a state socket.io never retries on its own).
   */
  reconnectIfNeeded(): void {
    if (!this.canReconnect()) {
      return;
    }

    const socket = this.socketSubject.getValue();
    if (!socket) {
      // No stored socket: connect() builds one (and no-ops if a connect is already in flight).
      this.connect();
      return;
    }

    if (socket.connected) {
      return;
    }

    if (socket.active) {
      // socket.io is mid-reconnect and will resend a fresh token via the auth callback; nudge it
      // rather than churning a new socket.
      socket.connect();
      return;
    }

    // Dead socket that will NOT auto-reconnect (server-initiated `unauthorized`/disconnect, or a
    // non-recoverable error). It still sits in `socketSubject`, so a plain connect() would early-return.
    // Tear it down and rebuild fresh — this also resets the base connection state (connectionPending /
    // connectionAttempt), clearing any wedge.
    this.disconnect();
    this.connect();
  }

  /**
   * Gate for reconnectIfNeeded(): subclasses that must not dial without credentials override this —
   * e.g. the manager requires a usable access token so a rebuild never hammers `Bearer undefined`
   * (which the backend rejects with `unauthorized` + a non-retrying disconnect).
   */
  protected canReconnect(): boolean {
    return true;
  }

  /**
   * Shared socket.io v4 lifecycle wiring — one implementation so the manager and public services
   * can't drift (they already had within one PR: only one of them handled `unauthorized`).
   *
   * - `connect` is transport-level only: it fires BEFORE the backend verifies the token (the
   *   gateway rejects bad tokens post-connection via `unauthorized`), so it is not proof of auth;
   *   it just hands the socket to the base service.
   * - `authenticated` is the real auth-success signal, emitted by the gateway after it verifies
   *   the token and joins the socket's rooms — on the initial connect AND every reconnect.
   * - Reconnection events live on the Manager (`socket.io`), not the Socket, in v4.
   */
  protected registerLifecycleHandlers(socket: Socket, observer: Subscriber<Socket>): void {
    // Legacy-backend fallback: a gateway that predates the `authenticated` ack never emits it,
    // which would leave recovery (refetch-on-reconnect + alert dismissal) permanently dead on a
    // newer client (backend rollback, or an iOS build shipping ahead of the backend deploy). If
    // nothing settles the handshake shortly after `connect`, treat the connection as
    // authenticated, the way pre-ack clients did. TODO: remove after one release cycle.
    let authAckFallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const cancelAuthAckFallback = (): void => {
      if (authAckFallbackTimer !== null) {
        clearTimeout(authAckFallbackTimer);
        authAckFallbackTimer = null;
      }
    };

    socket.on('connect', () => {
      cancelAuthAckFallback();
      authAckFallbackTimer = setTimeout(() => {
        this.logger.info('No authenticated ack after connect — assuming a legacy backend');
        this.onAuthenticatedSubject.next();
      }, this.authenticatedAckTimeoutMs);
      observer.next(socket);
      observer.complete();
    });

    socket.on(WebSocketEventsEnum.authenticated, () => {
      cancelAuthAckFallback();
      this.onAuthenticatedSubject.next();
    });

    // The backend rejects a missing/invalid token with `unauthorized` and then force-disconnects
    // (a server-initiated disconnect does not auto-reconnect). Surface it so the connection alert
    // reflects reality; recovery comes from reconnectIfNeeded() (resume / health-check).
    socket.on(WebSocketEventsEnum.unauthorized, (message) => {
      cancelAuthAckFallback();
      this.logger.info('Socket unauthorized:', message);
      this.onConnectionErrorSubject.next();
      observer.complete();
    });

    socket.on('connect_error', (error) => {
      this.logger.info('Socket connection error:', error?.message ?? error);
      this.onConnectionErrorSubject.next();
      // When socket.io will not auto-reconnect (e.g. a non-recoverable error), complete so the base
      // service clears `connectionPending` and a later connect()/reconnectIfNeeded() can rebuild.
      if (!socket.active) {
        observer.complete();
      }
    });

    socket.on('disconnect', (reason) => {
      cancelAuthAckFallback();
      this.logger.info('Socket connection disconnect:', reason);
      if (reason !== 'io client disconnect') {
        this.onConnectionErrorSubject.next();
      }
    });

    socket.on(WebSocketEventsEnum.sessionRevoked, () => this.onSessionRevokedSubject.next());

    // Recovery is driven by `authenticated` above (which fires on every reconnect), so the
    // manager-level reconnection events are log-only — except `reconnect_failed`, which means
    // socket.io has given up and only a rebuild can revive the connection.
    socket.io.on('reconnect', () => {
      this.logger.info('Socket reconnected');
    });

    socket.io.on('reconnect_error', (error) => {
      this.logger.info('Socket reconnection error:', error?.message ?? error);
    });

    socket.io.on('reconnect_failed', () => {
      this.logger.info('Socket reconnection failed after maximum attempts');
      this.onConnectionErrorSubject.next();
      observer.complete();
    });
  }

  /**
   * Teardown for a `createSocket()` observable: stop an in-flight socket when the subscription is torn
   * down before it connects (e.g. a superseding disconnect()). A connected socket must survive
   * completion, so only disconnect when it is not connected. Shared so the subclasses can't drift.
   */
  protected socketTeardown(socket: Socket): () => void {
    return () => {
      if (!socket.connected) {
        socket.disconnect();
      }
    };
  }

  private isWrappedPayload(payload: any): payload is WebSocketPayloadWithSequence {
    return (
      payload &&
      typeof payload === 'object' &&
      'sequenceNumber' in payload &&
      'date' in payload &&
      'data' in payload
    );
  }
}
